import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { isTablet, isMobile } from 'react-device-detect';
import heroDesktop from '../assets/atf-home-social-desktop.webp';
import heroMobile from '../assets/atf-home-social-mobile.webp';
import cryptoDesktop from '../assets/sliders/crypto-desktop.png';
import cryptoMobile from '../assets/sliders/crypto-mobile.png';
import stocksDesktop from '../assets/sliders/stocks-desktop.png';
import stocksMobile from '../assets/sliders/stocks-mobile.png';
import retirementDesktop from '../assets/sliders/retirement-desktop.png';
import retirementMobile from '../assets/sliders/retirement-mobile.png';

const Hero = () => {
  // Force iPad Pro view for testing - remove this line in production
  const forceIPadPro = window.innerWidth === 1024;
  
  // Device detection logic
  const isIPadPro = forceIPadPro || (isTablet && !isMobile && window.innerWidth >= 1024 && window.innerWidth <= 1279);
  const isDesktop = !isIPadPro && !isMobile && !isTablet;
  const isMobileDevice = isMobile || (isTablet && !isIPadPro);

  // If iPad Pro, render special hero
  if (isIPadPro) {
    return <IPadProHero />;
  }

  // Regular hero for other devices
  return <RegularHero isDesktop={isDesktop} isMobileDevice={isMobileDevice} />;
};

const IPadProHero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      title: "Advanced crypto trading & DeFi solutions.",
      subtitle: "Navigate the digital economy with cutting-edge blockchain technology and expert insights.",
      desktopImage: cryptoDesktop,
      mobileImage: cryptoMobile,
      buttonText: "Explore Crypto",
      buttonLink: "/apply-now"
    },
    {
      id: 2,
      title: "Your partner in crypto wealth-building.",
      subtitle: "Trusted by clients worldwide to earn interest and borrow funds with confidence.",
      desktopImage: heroDesktop,
      mobileImage: heroMobile,
      buttonText: "Sign up",
      buttonLink: "/apply-now"
    },
    {
      id: 3,
      title: "Professional stock trading & portfolio management.",
      subtitle: "Access global markets with institutional-grade tools and expert guidance.",
      desktopImage: stocksDesktop,
      mobileImage: stocksMobile,
      buttonText: "Start Trading",
      buttonLink: "/apply-now"
    },
    {
      id: 4,
      title: "Secure retirement planning & wealth preservation.",
      subtitle: "Build a lasting legacy with our comprehensive retirement and investment strategies.",
      desktopImage: retirementDesktop,
      mobileImage: retirementMobile,
      buttonText: "Plan Your Future",
      buttonLink: "/apply-now"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <section className="relative bg-amber-50 pt-16 lg:pt-20 h-[130vh] lg:h-screen overflow-hidden min-h-[130vh] lg:min-h-[100vh]">
      {/* Background Images - Desktop */}
      <AnimatePresence mode="wait">
        <motion.div 
          key={`desktop-${currentSlide}`}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="hidden xl:block absolute inset-0"
        >
          <img 
            src={currentSlideData.desktopImage} 
            alt="Hero background" 
            className="w-full h-full object-cover object-center lg:object-right"
            loading="eager"
          />
          <div className="absolute inset-0 bg-white/5"></div>
        </motion.div>
      </AnimatePresence>

      {/* Background Images - Mobile/Tablet (including iPad Pro) */}
      <AnimatePresence mode="wait">
        <motion.div 
          key={`mobile-${currentSlide}`}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="xl:hidden absolute inset-0"
        >
          <img 
            src={currentSlideData.mobileImage} 
            alt="Hero background" 
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black/20"></div>
        </motion.div>
      </AnimatePresence>

      {/* Content Overlay */}
      <div className="relative z-10 h-full flex flex-col">
        <div className="flex-1 flex items-start lg:items-center pt-12 lg:pt-0">
          {/* Content Container */}
          <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 -mt-80">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.8 }}
                className="space-y-6 md:space-y-8 lg:space-y-6 max-w-2xl lg:max-w-none"
              >
                <div className="space-y-4 md:space-y-6 lg:space-y-4">
                  <h1 className="text-7xl sm:text-8xl md:text-9xl lg:text-7xl xl:text-8xl font-bold leading-[1.1] text-gray-900 tracking-tight font-heading">
                    {currentSlideData.title.split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        {index < currentSlideData.title.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </h1>
                  
                  <p className="text-3xl md:text-4xl lg:text-3xl xl:text-3xl text-gray-700 leading-relaxed md:leading-relaxed lg:leading-relaxed font-medium max-w-xl lg:max-w-lg">
                    {currentSlideData.subtitle.split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        {index < currentSlideData.subtitle.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </p>
                </div>

                {/* Sign Up Button */}
                <Link to={currentSlideData.buttonLink}>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-gray-900 text-white px-12 py-6 md:px-14 md:py-7 lg:px-12 lg:py-6 xl:px-14 xl:py-7 rounded-2xl font-semibold inline-flex items-center gap-3 text-2xl md:text-3xl lg:text-2xl xl:text-3xl hover:bg-gray-800 transition-colors duration-200 w-auto shadow-lg"
                  >
                    {currentSlideData.buttonText}
                    <ArrowRightIcon className="w-7 h-7" />
                  </motion.button>
                </Link>

                {/* Private Wealth Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="bg-[#6f6e6c]/20 backdrop-blur-sm border-2 border-white/80 rounded-xl p-6 md:p-7 lg:p-6 xl:p-6 max-w-md lg:max-w-sm xl:max-w-md shadow-2xl"
                >
                  <p className="text-gray-800 font-semibold mb-3 text-xl lg:text-xl leading-tight">
                    Unlock personalized crypto solutions when you add{' '}
                    <span className="font-bold text-2xl">$100,000</span> or more.
                  </p>
                  <a 
                    href="#private" 
                    className="text-emerald-600 font-semibold inline-flex items-center gap-2 hover:text-emerald-700 transition-colors text-lg lg:text-lg"
                  >
                    Discover Segway Private
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </motion.div>
              </motion.div>

              {/* Right Content - iPad Stats/Desktop Overlay Cards */}
              <div className="hidden md:block relative">
                {/* iPad: Stats Cards */}
                <div className="lg:hidden">
                  <div className="grid grid-cols-1 gap-8">
                    {/* Stats Card 1 */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.6 }}
                      className="bg-[#6f6e6c]/20 backdrop-blur-sm rounded-3xl p-8 shadow-xl border-2 border-white/80"
                    >
                      <div className="text-center">
                        <div className="text-5xl font-bold text-gray-900 mb-3">Operating</div>
                        <div className="text-3xl font-semibold text-gray-600">since 2018</div>
                      </div>
                    </motion.div>

                    {/* Stats Card 2 */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.8 }}
                      className="bg-[#6f6e6c]/20 backdrop-blur-sm rounded-3xl p-8 shadow-xl border-2 border-white/80"
                    >
                      <div className="text-center">
                        <div className="text-5xl font-bold text-gray-900 mb-3">Personalized</div>
                        <div className="text-3xl font-semibold text-gray-600">client care 24/7</div>
                      </div>
                    </motion.div>

                    {/* Stats Card 3 */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 1.0 }}
                      className="bg-[#6f6e6c]/20 backdrop-blur-sm rounded-3xl p-8 shadow-xl border-2 border-white/80"
                    >
                      <div className="text-center">
                        <div className="text-5xl font-bold text-gray-900 mb-3">Assets under management</div>
                        <div className="text-3xl font-semibold text-gray-600">$1+ billion</div>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Desktop: Overlay Cards */}
                <div className="hidden lg:block">
                  {/* Testimonial Card - Upper Right */}
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="absolute top-20 right-0 bg-white/20 backdrop-blur-md rounded-lg p-4 w-[240px] h-[120px] shadow-lg border border-white/30 transform -translate-y-[100px] translate-x-[50px]"
                  >
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <div className="text-5xl text-gray-400 mb-3">"</div>
                      <p className="text-gray-700 text-base italic leading-relaxed">
                        Segway Capital are definitely a cut above the rest.
                      </p>
                    </div>
                  </motion.div>

                  {/* Trustpilot Rating Card - Below Testimonial */}
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.8 }}
                    className="absolute top-1/2 -translate-y-1/2 right-[100px] bg-white/20 backdrop-blur-md rounded-lg p-4 w-[180px] h-[140px] shadow-lg border border-white/30"
                  >
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <div className="font-bold text-gray-700 mb-3 text-lg">Rated Excellent</div>
                      <div className="flex justify-center mb-3">
                        <div className="flex space-x-1">
                          {[...Array(5)].map((_, i) => (
                            <button key={i} className="bg-emerald-500 rounded p-1 hover:bg-emerald-600 transition-colors">
                              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center justify-center text-gray-700 text-lg font-medium">
                        <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        Trustpilot
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Trustpilot Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="lg:hidden absolute bottom-20 left-4 right-4 bg-white/95 backdrop-blur-md rounded-2xl p-6 shadow-xl border border-white/50 w-[220px] h-[160px] mx-auto"
        >
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="font-bold text-gray-900 mb-4 text-xl">Rated Excellent</div>
            <div className="flex justify-center mb-4">
              <div className="flex space-x-1">
                {[...Array(5)].map((_, i) => (
                  <button key={i} className="bg-teal-500 rounded p-1 hover:bg-teal-600 transition-colors">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-center text-gray-900 text-lg font-medium">
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Trustpilot
            </div>
          </div>
        </motion.div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-white scale-125 shadow-lg' 
                  : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const RegularHero = ({ isDesktop, isMobileDevice }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      title: "Advanced crypto trading & DeFi solutions.",
      subtitle: "Navigate the digital economy with cutting-edge blockchain technology and expert insights.",
      desktopImage: cryptoDesktop,
      mobileImage: cryptoMobile,
      buttonText: "Explore Crypto",
      buttonLink: "/apply-now"
    },
    {
      id: 2,
      title: "Your partner in crypto wealth-building.",
      subtitle: "Trusted by clients worldwide to earn interest and borrow funds with confidence.",
      desktopImage: heroDesktop,
      mobileImage: heroMobile,
      buttonText: "Sign up",
      buttonLink: "/apply-now"
    },
    {
      id: 3,
      title: "Professional stock trading & portfolio management.",
      subtitle: "Access global markets with institutional-grade tools and expert guidance.",
      desktopImage: stocksDesktop,
      mobileImage: stocksMobile,
      buttonText: "Start Trading",
      buttonLink: "/apply-now"
    },
    {
      id: 4,
      title: "Secure retirement planning & wealth preservation.",
      subtitle: "Build a lasting legacy with our comprehensive retirement and investment strategies.",
      desktopImage: retirementDesktop,
      mobileImage: retirementMobile,
      buttonText: "Plan Your Future",
      buttonLink: "/apply-now"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <section className="relative bg-amber-50 pt-16 lg:pt-20 h-[130vh] lg:h-screen overflow-hidden min-h-[130vh] lg:min-h-[100vh]">
      {/* Background Images - Desktop */}
      <AnimatePresence mode="wait">
        <motion.div 
          key={`desktop-${currentSlide}`}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="hidden xl:block absolute inset-0"
        >
          <img 
            src={currentSlideData.desktopImage} 
            alt="Hero background" 
            className="w-full h-full object-cover object-center lg:object-right"
            loading="eager"
          />
          <div className="absolute inset-0 bg-white/5"></div>
        </motion.div>
      </AnimatePresence>

      {/* Background Images - Mobile/Tablet */}
      <AnimatePresence mode="wait">
        <motion.div 
          key={`mobile-${currentSlide}`}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="xl:hidden absolute inset-0"
        >
          <img 
            src={currentSlideData.mobileImage} 
            alt="Hero background" 
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black/20"></div>
        </motion.div>
      </AnimatePresence>

      {/* Content Overlay */}
      <div className="relative z-10 h-full flex flex-col">
        <div className="flex-1 flex items-start lg:items-center pt-12 lg:pt-0">
          <div className="max-w-7xl mx-auto pl-2 pr-4 sm:px-2 md:px-6 lg:pl-0 lg:pr-4 lg:ml-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-start md:items-center lg:items-center">
              {/* Content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={`content-${currentSlide}`}
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 50 }}
                  transition={{ duration: 0.8 }}
                  className="space-y-6 md:space-y-8 lg:space-y-6 max-w-lg md:max-w-xl lg:max-w-xl md:pr-4"
                >
                  <div className="space-y-4 md:space-y-6 lg:space-y-4">
                    <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-5xl xl:text-6xl font-medium leading-[1.1] text-gray-900 tracking-tight font-heading">
                      {currentSlideData.title.split('\n').map((line, index) => (
                        <React.Fragment key={index}>
                          {line}
                          {index < currentSlideData.title.split('\n').length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </h1>
                    
                    <p className="text-lg md:text-xl lg:text-lg xl:text-xl text-gray-700 leading-relaxed md:leading-relaxed lg:leading-relaxed font-medium">
                      {currentSlideData.subtitle.split('\n').map((line, index) => (
                        <React.Fragment key={index}>
                          {line}
                          {index < currentSlideData.subtitle.split('\n').length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </p>
                  </div>

                  {/* Sign Up Button */}
                  <Link to={currentSlideData.buttonLink}>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="bg-gray-900 text-white px-8 py-4 md:px-10 md:py-5 lg:px-8 lg:py-4 xl:px-10 xl:py-5 rounded-2xl font-medium inline-flex items-center gap-3 text-lg md:text-xl lg:text-lg xl:text-xl hover:bg-gray-800 transition-colors duration-200 w-auto"
                    >
                      {currentSlideData.buttonText}
                      <ArrowRightIcon className="w-4 h-4" />
                    </motion.button>
                  </Link>

                  {/* Private Wealth Card */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                                          className="bg-[#6f6e6c]/20 backdrop-blur-sm border-2 border-white/80 rounded-xl p-3 md:p-4 lg:p-3 xl:p-4 max-w-[320px] md:max-w-[500px] lg:max-w-[360px] xl:max-w-[400px] shadow-xl"
                  >
                    <p className="text-gray-800 font-medium mb-2 text-sm lg:text-sm leading-tight">
                      Unlock personalized crypto solutions when you add{' '}
                      <span className="font-bold">$100,000</span> or more.
                    </p>
                    <a 
                      href="#private" 
                      className="text-emerald-600 font-medium inline-flex items-center gap-1 hover:text-emerald-700 transition-colors text-xs lg:text-xs"
                    >
                      Discover Segway Private
                      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </motion.div>
                </motion.div>
              </AnimatePresence>

              {/* Right Content - Stats/Overlay Cards */}
              <div className="hidden md:block relative">
                {/* Mobile/Tablet: Stats Cards */}
                <div className="xl:hidden">
                  <div className="grid grid-cols-1 gap-6">
                    {/* Stats Card 1 */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.6 }}
                      className="bg-[#6f6e6c]/20 backdrop-blur-sm rounded-2xl p-6 shadow-lg border-2 border-white/80"
                    >
                      <div className="text-center">
                        <div className="text-3xl font-bold text-gray-900 mb-2">Operating</div>
                        <div className="text-xl font-semibold text-gray-600">since 2018</div>
                      </div>
                    </motion.div>

                    {/* Stats Card 2 */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.8 }}
                      className="bg-[#6f6e6c]/20 backdrop-blur-sm rounded-2xl p-6 shadow-lg border-2 border-white/80"
                    >
                      <div className="text-center">
                        <div className="text-3xl font-bold text-gray-900 mb-2">Personalized</div>
                        <div className="text-xl font-semibold text-gray-600">client care 24/7</div>
                      </div>
                    </motion.div>

                                        {/* Stats Card 3 */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 1.0 }}
                      className="bg-[#6f6e6c]/20 backdrop-blur-sm rounded-2xl p-6 shadow-xl border-2 border-white/80"
                    >
                      <div className="text-center">
                        <div className="text-3xl font-bold text-gray-900 mb-2">Assets under management</div>
                        <div className="text-xl font-semibold text-gray-600">$1+ billion</div>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Desktop: Overlay Cards */}
                <div className="hidden xl:block">
                  {/* Testimonial Card - Upper Right */}
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="absolute top-20 right-0 bg-white/20 backdrop-blur-md rounded-lg p-4 w-[240px] h-[120px] shadow-lg border border-white/30 transform -translate-y-[100px] translate-x-[50px]"
                  >
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <div className="text-5xl text-gray-400 mb-3">"</div>
                      <p className="text-gray-700 text-base italic leading-relaxed">
                        Segway Capital are definitely a cut above the rest.
                      </p>
                    </div>
                  </motion.div>

                  {/* Trustpilot Rating Card - Below Testimonial */}
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.8 }}
                    className="absolute top-1/2 -translate-y-1/2 right-[100px] bg-white/20 backdrop-blur-md rounded-lg p-4 w-[180px] h-[140px] shadow-lg border border-white/30"
                  >
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <div className="font-bold text-gray-700 mb-3 text-lg">Rated Excellent</div>
                      <div className="flex justify-center mb-3">
                        <div className="flex space-x-1">
                          {[...Array(5)].map((_, i) => (
                            <button key={i} className="bg-emerald-500 rounded p-1 hover:bg-emerald-600 transition-colors">
                              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center justify-center text-gray-700 text-lg font-medium">
                        <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        Trustpilot
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Trustpilot Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="xl:hidden absolute bottom-20 left-4 right-4 bg-white/20 backdrop-blur-md rounded-lg p-[15px] shadow-lg border border-white/30 w-[180px] h-[140px] mx-auto"
        >
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="font-bold text-gray-900 mb-3 text-lg">Rated Excellent</div>
            <div className="flex justify-center mb-3">
              <div className="flex space-x-1">
                {[...Array(5)].map((_, i) => (
                  <button key={i} className="bg-teal-500 rounded p-1 hover:bg-teal-600 transition-colors">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-center text-gray-900 text-lg font-medium">
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Trustpilot
            </div>
          </div>
        </motion.div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-white scale-125 shadow-lg' 
                  : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;